import { z } from 'zod';
import { canonicalJson } from '../macroPackage/canonicalJson';
import { graphJsonObjectSchema, type GraphJson, type GraphJsonObject } from './primitives';

const schemaKeys = new Set(['type', 'properties', 'required', 'additionalProperties', 'items', 'enum', 'const',
  'minItems', 'maxItems', 'minLength', 'maxLength', 'minimum', 'maximum', 'description', 'title']);
const schemaTypes = new Set(['null', 'boolean', 'string', 'number', 'integer', 'array', 'object']);

/** The supported declarative subset has no remote references or executable validators. */
export class GraphAttributeValidator {
  public validateSchema(schema: GraphJsonObject): string[] {
    const errors: string[] = [];
    this.inspectSchema(schema, '$', errors);
    return errors;
  }

  public validate(value: GraphJson, schema: GraphJsonObject): string[] {
    const errors = this.validateSchema(schema);
    if (!errors.length) this.inspectValue(value, schema, '$', errors);
    return errors;
  }

  private inspectSchema(schema: GraphJsonObject, path: string, errors: string[]): void {
    for (const key of Object.keys(schema)) if (!schemaKeys.has(key)) errors.push(`${path}: unsupported schema keyword ${key}`);
    const types = Array.isArray(schema["type"]) ? schema["type"] : [schema["type"]];
    if (!types.length || types.some(type => typeof type !== 'string' || !schemaTypes.has(type))) errors.push(`${path}: explicit supported type required`);
    if (schema["properties"] !== undefined) {
      const properties = graphJsonObjectSchema.safeParse(schema["properties"]);
      if (!properties.success) errors.push(`${path}: invalid properties`);
      else for (const [name, child] of Object.entries(properties.data)) this.inspectChild(child, `${path}.${name}`, errors);
    }
    if (schema["items"] !== undefined) this.inspectChild(schema["items"], `${path}[]`, errors);
    if (schema["required"] !== undefined && (!Array.isArray(schema["required"]) || schema["required"].some(item => typeof item !== 'string'))) {
      errors.push(`${path}: required must be strings`);
    }
    if (schema["additionalProperties"] !== undefined && typeof schema["additionalProperties"] !== 'boolean') errors.push(`${path}: invalid additionalProperties`);
    if (schema["enum"] !== undefined && !Array.isArray(schema["enum"])) errors.push(`${path}: enum must be an array`);
    for (const key of ['minItems', 'maxItems', 'minLength', 'maxLength', 'minimum', 'maximum']) {
      if (schema[key] !== undefined && typeof schema[key] !== 'number') errors.push(`${path}: ${key} must be numeric`);
    }
  }

  private inspectChild(child: GraphJson, path: string, errors: string[]): void {
    const parsed = graphJsonObjectSchema.safeParse(child);
    if (!parsed.success) errors.push(`${path}: schema must be an object`);
    else this.inspectSchema(parsed.data, path, errors);
  }

  private inspectValue(value: GraphJson, schema: GraphJsonObject, path: string, errors: string[]): void {
    const types = Array.isArray(schema["type"]) ? schema["type"] : [schema["type"]];
    if (!types.some(type => this.hasType(value, type))) { errors.push(`${path}: type mismatch`); return; }
    if (schema["const"] !== undefined && canonicalJson(value) !== canonicalJson(schema["const"])) errors.push(`${path}: const mismatch`);
    if (Array.isArray(schema["enum"]) && !schema["enum"].some(item => canonicalJson(item) === canonicalJson(value))) errors.push(`${path}: enum mismatch`);
    if (typeof value === 'number') this.range(value, schema["minimum"], schema["maximum"], path, errors);
    if (typeof value === 'string') this.range(value.length, schema["minLength"], schema["maxLength"], path, errors);
    if (Array.isArray(value)) {
      this.range(value.length, schema["minItems"], schema["maxItems"], path, errors);
      if (schema["items"]) value.forEach((item, index) => this.inspectValue(item, schema["items"] as GraphJsonObject, `${path}[${index}]`, errors));
    } else if (value !== null && typeof value === 'object') this.inspectObject(value, schema, path, errors);
  }

  private inspectObject(value: GraphJsonObject, schema: GraphJsonObject, path: string, errors: string[]): void {
    const properties = (schema["properties"] ?? {}) as GraphJsonObject;
    for (const name of (schema["required"] ?? []) as string[]) {
      if (!Object.prototype.hasOwnProperty.call(value, name)) errors.push(`${path}.${name}: required`);
    }
    for (const [name, item] of Object.entries(value)) {
      if (Object.prototype.hasOwnProperty.call(properties, name)) this.inspectValue(item, properties[name] as GraphJsonObject, `${path}.${name}`, errors);
      else if (schema["additionalProperties"] === false) errors.push(`${path}.${name}: unknown property`);
    }
  }

  private range(value: number, minimum: GraphJson | undefined, maximum: GraphJson | undefined, path: string, errors: string[]): void {
    if ((typeof minimum === 'number' && value < minimum) || (typeof maximum === 'number' && value > maximum)) errors.push(`${path}: outside schema range`);
  }

  private hasType(value: GraphJson, type: GraphJson | undefined): boolean {
    if (type === 'null') return value === null;
    if (type === 'array') return Array.isArray(value);
    if (type === 'integer') return typeof value === 'number' && Number.isSafeInteger(value);
    if (type === 'object') return value !== null && typeof value === 'object' && !Array.isArray(value);
    return typeof value === type;
  }
}

export const graphAttributeSchema = graphJsonObjectSchema.superRefine((value, context) => {
  for (const message of new GraphAttributeValidator().validateSchema(value)) context.addIssue({ code: z.ZodIssueCode.custom, message });
});
